import { FC, useEffect, useState, useRef} from "react";
import { audienceNameIssues } from "src/shared/utils/validation/networkTextRules";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { AudienceBuilderService } from "@services/engagements/launch/audience/audienceBuilderService";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import { audienceBuilderStore } from "./audienceBuilderStore";
import { launchActions } from "src/modules/engagement/launchStore";
import { AudienceRuleBuilder, type RuleGroup } from "./AudienceRuleBuilder";
import { useAudienceCalculation } from "./useAudienceCalculation";
import "./audience.scss";
import { RecordId } from "@/shared/components/recordId/RecordId";
import { ReuploadEveryoneButton } from "./ReuploadEveryoneButton";
import { CHANNEL_HOLDOUT, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import type { AudienceChannelHoldout } from "@models/audienceDefinition.model";

interface Props {
  audience: any;
  organizationId: number;
  onBack: () => void;
}

/*
    One audience, in full: what it targets, the rules that define it, the
    numbers those rules produce, and the people they resolve to. This replaces
    the edit modal — an audience is a working surface, not a dialog.
*/
const AudienceDetail: FC<Props> = ({ audience, organizationId, onBack }) => {
  const { previewCount, isLoadingPreview, entities, isLoadingEntities } =
    useSnapshot(audienceBuilderStore);
  const { calculate, calculatingId, resultById, error: calcError, setError: setCalcError } =
    useAudienceCalculation();

  const [name, setName] = useState(audience.name);
  // Shown once per name. Re-warning on the same text would make Save look broken --
  // click, dismiss, click, dismiss -- and the operator has already answered.
  const [pendingWarning, setPendingWarning] = useState<string | null>(null);
  const acknowledgedName = useRef<Set<string>>(new Set());
  const [rules, setRules] = useState<RuleGroup>(
    audience.rules && Object.keys(audience.rules).length > 0
      ? audience.rules
      : { id: "root", combinator: "AND", rules: [] },
  );
  const [showJson, setShowJson] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    new AudienceBuilderService().loadEntities(organizationId, audience.id);
  }, [organizationId, audience.id]);

  const isCalculating = calculatingId === audience.id;
  const result = resultById.get(audience.id);

  const totalMembers = result ? result.total_active_members : audience.total_members ?? 0;
  const totalActive = result
    ? result.total_active ?? result.total_active_members
    : audience.total_targetable ?? 0;
  const totalConverted = result ? result.total_converted ?? 0 : audience.total_converted ?? 0;
  // A channel holdout splits Active in two for that channel (MPN-2078): the people still
  // shown ads everywhere but there. One tile per channel, after Active, and only while a
  // holdout is applied -- with none the row is Members, Active, Converted as before.
  const channelHoldoutOn = useFeatureFlag(CHANNEL_HOLDOUT);
  const channelHoldouts: AudienceChannelHoldout[] =
    (result ? result.channel_holdouts : audience.channel_holdouts) ?? [];
  const shareOfActive = (count: number) =>
    totalActive > 0 ? `${Math.round((100 * count) / totalActive)}% of active` : null;

  const handlePreview = async () => {
    setError(null);
    try {
      await new AudienceBuilderService().previewAudience(audience.id, rules);
    } catch {
      setError("Could not preview the count.");
    }
  };

  // Derived on every render, so an apostrophe already in a saved name is visible
  // without touching the field.
  const nameIssues = audienceNameIssues(name);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Give this audience a name.");
      return;
    }
    // Acknowledge, then save (MPN-1616). This field has a Save action, so the warning is
    // a popup rather than the inline treatment the Ad Copy fields get. It warns; it does
    // not block -- the name is still saved once the operator has seen it, because the
    // apostrophe only breaks Google and the audience may not publish there.
    if (nameIssues.length && !acknowledgedName.current.has(name.trim())) {
      acknowledgedName.current.add(name.trim());
      setPendingWarning(nameIssues[0]);
      return;
    }
    setPendingWarning(null);
    setIsSaving(true);
    setError(null);
    try {
      await new AudienceDefinitionService().updateAudience(audience.id, {
        name: name.trim(),
        rules,
      });
      launchActions.triggerAudienceRefresh();
    } catch {
      setError("Could not save this audience.");
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty =
    name !== audience.name ||
    JSON.stringify(rules) !== JSON.stringify(audience.rules ?? {});

  return (
    <div>
      {/*
          Acknowledge-then-save (MPN-1616). Dismissing it does not save; the operator
          clicks Save again, which now goes through. That keeps one meaning for the Save
          button rather than having the dialog quietly perform the action behind it.
      */}
      {pendingWarning && (
        <div className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 small" role="alert">
          <i className="bi bi-exclamation-triangle mt-1" />
          <div className="flex-grow-1">{pendingWarning}</div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: "0.72rem" }}
            onClick={() => setPendingWarning(null)}
          >
            Got it
          </button>
        </div>
      )}

      <div className="audience-detail__head">
        <button type="button" className="audience-detail__back" onClick={onBack}>
          <i className="bi bi-chevron-left me-1" />
          Audiences
        </button>
        <span className="audience-detail__name">{audience.name}</span>
        <RecordId label="Audience" id={audience.id} />
        <div className="d-flex align-items-center gap-2 ms-auto">
          <ReuploadEveryoneButton audience={audience} />
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => calculate(audience.id, audience.name)}
            disabled={isCalculating}
            style={{ fontSize: "0.72rem" }}
          >
            {isCalculating ? "Calculating…" : "Calculate members"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            style={{ fontSize: "0.72rem" }}
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {(error || calcError) && (
        <div className="alert alert-danger py-2 small">
          {error ?? calcError}
          <button
            type="button"
            className="btn-close float-end"
            style={{ fontSize: "0.6rem" }}
            onClick={() => {
              setError(null);
              setCalcError(null);
            }}
          />
        </div>
      )}

      <div className="audience-stats">
        <div className="audience-stats__stat">
          <div className="audience-stats__label">Members</div>
          <div className="audience-stats__value">{totalMembers.toLocaleString()}</div>
        </div>
        <div className="audience-stats__stat">
          <div className="audience-stats__label">Active</div>
          <div className="audience-stats__value">{totalActive.toLocaleString()}</div>
          <div className="audience-stats__hint">being shown ads</div>
        </div>
        {channelHoldoutOn &&
          channelHoldouts.map((h) => (
            <div
              key={h.channel}
              className="audience-stats__stat audience-stats__stat--channel-holdout"
              data-channel={h.channel}
            >
              <div className="audience-stats__label">
                Active · Channel holdout
                <span className="audience-stats__tag">{h.label}</span>
              </div>
              <div className="audience-stats__value">
                {h.active_count.toLocaleString()}
                {shareOfActive(h.active_count) && (
                  <span className="audience-stats__share">{shareOfActive(h.active_count)}</span>
                )}
              </div>
              <div className="audience-stats__hint">being shown ads, excluding {h.label}</div>
            </div>
          ))}
        <div className="audience-stats__stat">
          <div className="audience-stats__label">Converted</div>
          <div className="audience-stats__value">{totalConverted.toLocaleString()}</div>
        </div>
      </div>

      <div className="audience__section mb-3">
        <div className="audience__head">
          <i className="bi bi-sliders" style={{ color: "#64748b" }} />
          <span className="audience__title">Definition</span>
        </div>
        <div className="audience__body">
          {/*
              The warning is shown here as well as on save (MPN-1616). Save is disabled
              until something changes, so an audience that already carries an apostrophe
              -- which is most of the ones that have one -- could never reach the save
              popup at all: open the page, click nothing, see nothing. QA reported it
              missing for exactly that reason.

              Beside the label, in red, matching the Ad Copy fields. The popup on save
              stays, because the ticket asks for an acknowledgement before the write.
          */}
          <label className="audience-field__label">
            Name
            {nameIssues.map((issue) => (
              <span key={issue} style={{ marginLeft: 6, color: "#dc2626", fontWeight: 400 }}>
                {issue}
              </span>
            ))}
          </label>
          <input
            className="form-control mb-3"
            style={nameIssues.length ? { borderColor: "#dc2626" } : undefined}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Audience name"
          />
        </div>
      </div>

      <div className="audience__section mb-3">
        <div className="audience__head">
          <i className="bi bi-funnel" style={{ color: "#64748b" }} />
          <span className="audience__title">Rules</span>
          {previewCount !== null && (
            <span className="audience__count">{previewCount.toLocaleString()} match</span>
          )}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowJson((v) => !v)}
              style={{ fontSize: "0.72rem" }}
            >
              {showJson ? "Builder" : "JSON"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handlePreview}
              disabled={isLoadingPreview || isLoadingEntities}
              style={{ fontSize: "0.72rem" }}
            >
              {isLoadingPreview ? "Counting…" : "Preview count"}
            </button>
          </div>
        </div>
        <div className="audience__body">
          {isLoadingEntities ? (
            <Loader />
          ) : showJson ? (
            <textarea
              className="form-control"
              rows={18}
              readOnly
              value={JSON.stringify(rules, null, 2)}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          ) : entities.length === 0 ? (
            <div className="audience__note">
              No entities available — this organization needs an ingest configuration before rules
              can be built.
            </div>
          ) : (
            <AudienceRuleBuilder rules={rules} onChange={setRules} />
          )}
        </div>
      </div>

    </div>
  );
};

export default AudienceDetail;
